import { Company, CompanyType, Property } from 'account-hybrid/core/authentication';
import { CompanySpace, MixedSpace, PropertySpace, SpaceBase, SpacesHistoryItem, SpaceType } from '../..';
import { SpacesActions, SpacesActionTypes } from '../actions/spaces.actions';

declare const IntersectionObserver;

export class VisitedSpace {
    history: SpacesHistoryItem;
    space: SpaceBase;
}

export interface State {
    isIntersectionObserverApiSupported: boolean;
    isVisible: boolean;
    isCancelVisible: boolean;
    isInitialized: boolean;
    searchCriteria: string;
    pageSize: number;
    pageIndex: number;
    properties: Property[];
    companies: Company[];
    allSpaces: SpaceBase[];
    allAccessibleSpaces: SpaceBase[];
    collaborativeSpaces: SpaceBase[];
    filteredSpaces: SpaceBase[];
    history: VisitedSpace[];
}

export const initialState: State = {
    isIntersectionObserverApiSupported: typeof IntersectionObserver !== 'undefined',
    isVisible: false,
    isCancelVisible: true,
    isInitialized: false,
    searchCriteria: sessionStorage.getItem('SpacesSearchCriteria'),
    pageSize: 5,
    pageIndex: 0,
    properties: [],
    companies: [],
    allSpaces: [],
    allAccessibleSpaces: [],
    collaborativeSpaces: [],
    filteredSpaces: [],
    history: []
};

export function spaceLookup(space: SpaceBase, historyItem: SpacesHistoryItem) {
    switch (space.type) {
        case SpaceType.Collaborative:
        case SpaceType.Property:
        case SpaceType.Company: {
            return space.id === historyItem.id;
        }
        case SpaceType.Mixed: {
            return (space as MixedSpace);
        }
    }
}

export function reducer(state = initialState, action: SpacesActions) {
    switch (action.type) {
        case SpacesActionTypes.Initialize: {
            const allSpaces = createSpaces(action.payload);
            const spacesById = new Map(allSpaces.map(s => [s.id, s]));
            const companiesById = new Map(action.payload.companies.map(c => [c.UUID, c]));
            const allAccessibleSpaces = allSpaces
                .filter(space => space.type !== SpaceType.Collaborative)
                .filter(space => (space.permissions ?? 0) != 0);
            const collaborativeSpaces = allSpaces.filter(space => space.type === SpaceType.Collaborative)
                .map(space => {
                    const collaborativeCompany = companiesById.get(space.id);
                    space.sponsorName = collaborativeCompany?.Settings?.SponsorProperty?.Name;
                    space.description = collaborativeCompany?.Settings?.Description;
                    space.destinationName = collaborativeCompany?.Settings?.DestinationDisplayName;
                    return space;
                });
            const filteredSpaces = state.isIntersectionObserverApiSupported ?
                getFilteredAndPagedSpaces(allAccessibleSpaces, null, 0, state.pageSize) :
                getFilteredSpaces(allAccessibleSpaces, null);
            const history = action.payload.history
                .reduce<VisitedSpace[]>((acc, item) => {
                    const space = spacesById.get(item.id);
                    if (space) acc.push({ history: item, space });
                    return acc;
                }, [])
                .sort((a, b) => b.history.timesVisited - a.history.timesVisited);
            return {
                ...state,
                isInitialized: true, ...action.payload,
                allSpaces,
                allAccessibleSpaces,
                collaborativeSpaces,
                filteredSpaces,
                history
            };
        }
        case SpacesActionTypes.SearchCriteriaChanged: {
            const filteredSpaces = state.isIntersectionObserverApiSupported ?
                getFilteredAndPagedSpaces(state.allAccessibleSpaces, action.payload, 0, state.pageSize) :
                getFilteredSpaces(state.allAccessibleSpaces, action.payload);
            return {
                ...state,
                pageIndex: 0,
                searchCriteria: action.payload,
                filteredSpaces,
                isSearchCriteriaChanging: false
            };
        }
        case SpacesActionTypes.SetIsVisible: {
            return { ...state, isVisible: action.payload };
        }
        case SpacesActionTypes.SetIsCancelVisible: {
            return { ...state, isCancelVisible: action.payload };
        }
        case SpacesActionTypes.FetchNextPage: {
            const nextPageResults = getFilteredAndPagedSpaces(state.allAccessibleSpaces, state.searchCriteria, state.pageIndex + 1, state.pageSize);
            const allFetchedResults = [...state.filteredSpaces, ...nextPageResults];
            return { ...state, pageIndex: state.pageIndex + 1, filteredSpaces: allFetchedResults };
        }
        default:
            return state;
    }
}


function companyToSpace(company: Company, companyProperties: Property[], indirectMatchSearchFields: string[] = []): CompanySpace {
    return {
        id: company.UUID,
        title: company.Name,
        slug: company.Code,
        type: company.Type == CompanyType.Collaborative ? SpaceType.Collaborative : SpaceType.Company,
        directMatchSearchFields: [
            company.Code,
            company.Name
        ],
        indirectMatchSearchFields: [
            ...companyProperties.map(p => p.Name),
            ...companyProperties.map(p => p.Slug),
            ...indirectMatchSearchFields
        ],
        backgroundImageUrl: company.BackgroundImageUrl,
        icon: company.DarkBackgroundLogoUrl || company.LightBackgroundLogoUrl,
        permissions: company.Permissions,
        createDate: company.CreateDate
    };
}

function propertyToSpace(property: Property, propertyCompanies: Company[]): PropertySpace {
    return {
        id: property.UUID,
        title: property.Name,
        slug: property.Slug,
        type: SpaceType.Property,
        directMatchSearchFields: [
            property.Slug,
            property.Name
        ],
        indirectMatchSearchFields: [
            ...propertyCompanies.map(p => p.Name),
            ...propertyCompanies.map(p => p.Code)
        ],
        backgroundImageUrl: property.BackgroundImageUrl,
        icon: property.DarkBackgroundLogoUrl || property.LightBackgroundLogoUrl,
        permissions: property.Permissions,
        createDate: property.CreateDate
    };
}

function companyAndPropertyToSpace(company: Company, property: Property, indirectMatchSearchFields: string[] = []): MixedSpace {
    return {
        id: company.UUID, // taking company.UUID as mixed space id
        title: company.Name,
        slug: `${company.Code}-${property.Slug}`,
        type: SpaceType.Mixed,
        directMatchSearchFields: [property.Slug, property.Name, company.Name, company.Code],
        indirectMatchSearchFields,
        backgroundImageUrl: property.BackgroundImageUrl,
        icon: property.DarkBackgroundLogoUrl || property.LightBackgroundLogoUrl,
        permissions: company.Permissions | property.Permissions,
        property: propertyToSpace(property, [company]),
        company: companyToSpace(company, [property])
    };
}


export function createSpaces(state: {
    companies: Company[],
    properties: Property[],
    history: SpacesHistoryItem[]
}, skipSorting = false) {

    const companies = state.companies || [];
    const properties = state.properties || [];
    const history = state.history || [];

    // Build lookup maps once — avoids repeated .find() scans in nested loops
    const propertyByUuid = new Map<string, Property>(properties.map(p => [p.UUID, p]));
    const historyById = new Map<string, SpacesHistoryItem>(history.map(h => [h.id, h]));

    const mixedCompaniesList = companies
        .filter(c =>
            c.Type !== CompanyType.Collaborative &&
            (c.ActiveAssociatedPropertiesCount === 1 &&
                c.Associations.length === 1 &&
                c.Associations.every(a => propertyByUuid.get(a)?.Name === c.Name)) ||
            c.Associations.some(a => !!propertyByUuid.get(a)?.IsVirtual));

    const mixedCompanyUuids = new Set(mixedCompaniesList.map(c => c.UUID));
    const mixedSingleAssociationUuids = new Set(
        mixedCompaniesList
            .filter(c => c.Associations.length === 1)
            .flatMap(c => c.Associations),
    );

    const companiesList = companies
        .filter(c => !mixedCompanyUuids.has(c.UUID));
    const propertiesList = properties
        .filter(p => !p.IsVirtual && !mixedSingleAssociationUuids.has(p.UUID));

    const mixedSpaces = mixedCompaniesList
        .map(c => {
            const associationSet = new Set(c.Associations);
            const companyProperties = properties.filter(p => associationSet.has(p.UUID));
            const property = companyProperties.length === 1 ? companyProperties[0] : companyProperties.find(p => p.IsVirtual);
            const realProperties = companyProperties.filter(p => !p.IsVirtual);

            const associatedCompanies = property ? companies
                .filter(company => company.Associations.includes(property.UUID)) : [];
            const indirectMatches = [
                ...associatedCompanies.map(s => s.Code),
                ...associatedCompanies.map(s => s.Name),
                ...realProperties.map(p => p.Name),
                ...realProperties.map(p => p.Slug)
            ];
            return companyAndPropertyToSpace(c, property, indirectMatches);
        });

    // Build a reverse index: propertyUuid -> companies that list it in Associations
    const companiesByAssociation = new Map<string, Company[]>();
    for (const c of companiesList) {
        for (const a of c.Associations) {
            let list = companiesByAssociation.get(a);
            if (!list) { list = []; companiesByAssociation.set(a, list); }
            list.push(c);
        }
    }

    const propertiesSpaces = propertiesList.map(property =>
        propertyToSpace(property, companiesByAssociation.get(property.UUID) || [])
    );

    const companiesSpaces = companiesList
        .map(c => {
            const companyProperties: Property[] = c.Associations
                .map(uuid => propertyByUuid.get(uuid))
                .filter((p): p is Property => !!p);
            return companyToSpace(c, companyProperties);
        })
        .filter(c => !!c);

    const allSpaces = [
        ...propertiesSpaces,
        ...companiesSpaces,
        ...mixedSpaces
    ];

    for (const space of allSpaces) {
        space.timesVisited = historyById.get(space.id)?.timesVisited ?? 0;
    }

    return skipSorting
        ? allSpaces
        : allSpaces.sort((a, b) =>
            (b.timesVisited ?? 0) !== (a.timesVisited ?? 0)
                ? (b.timesVisited ?? 0) - (a.timesVisited ?? 0)
                : a.title.localeCompare(b.title)
        );
}

function getFilteredSpaces(allSpaces: SpaceBase[], searchCriteria: string): SpaceBase[] {
    if (!searchCriteria) {
        return allSpaces;
    }
    try {
        const reCriteria = new RegExp(`${searchCriteria}`, 'gi');
        return allSpaces.filter(s => s.directMatchSearchFields.some(sf => reCriteria.test(sf)));
    } catch {
        return allSpaces;
    }

}

function isCompanyOrMixedSpace(space: SpaceBase) {
    return space.type === SpaceType.Company || space.type === SpaceType.Mixed;
}

function getFilteredAndPagedSpaces(allSpaces: SpaceBase[], searchCriteria: string, pageIndex: number, pageSize: number): SpaceBase[] {
    const reCriteria = new RegExp(`${searchCriteria}`, 'gi');
    const filtered = !!searchCriteria ?
        allSpaces
            .map(space => {
                return {
                    space,
                    isDirectMatch: space.directMatchSearchFields.some(sf => reCriteria.test(sf)),
                    isIndirectMatch: space.indirectMatchSearchFields.some(sf => reCriteria.test(sf))
                };
            })
            .filter(o => o.isDirectMatch || o.isIndirectMatch)
            .sort((aMatch, bMatch) => {
                if (aMatch.isDirectMatch && bMatch.isDirectMatch) {
                    if (isCompanyOrMixedSpace(aMatch.space) && isCompanyOrMixedSpace(bMatch.space)) {
                        return aMatch.space.title.localeCompare(bMatch.space.title);
                    } else if (isCompanyOrMixedSpace(aMatch.space)) {
                        return -1;
                    } else if (isCompanyOrMixedSpace(bMatch.space)) {
                        return 1;
                    }
                } else if (aMatch.isDirectMatch && !bMatch.isDirectMatch) {
                    return -1;
                } else if (!aMatch.isDirectMatch && bMatch.isDirectMatch) {
                    return 1;
                } else if (!aMatch.isDirectMatch && !bMatch.isDirectMatch) {
                    return aMatch.space.title.localeCompare(bMatch.space.title);
                }
            })
            .map(o => o.space) :
        allSpaces;

    // create new space object to make angular rerender the list to fix lazyload scroll issue
    return filtered.slice(pageSize * pageIndex, pageSize * pageIndex + pageSize)
        .map(space => {
            return { ...space };
        });
}
